
import { mapGetters } from 'vuex'

// Constants
const JOB_TYPE_ENUM = {
  Full_Time: 'FULL_TIME',
  Graduate: 'OTHER',
  Internship: 'INTERN',
  Part_Time: 'PART_TIME',
  Summer_Camp: 'TEMPORARY',
  Temporary: 'TEMPORARY',
  Voluntary: 'VOLUNTEER'
}

const SALARY_PER_ENUM = {
  '1': 'HOUR',
  '2': 'DAY',
  '3': 'WEEK',
  '4': 'MONTH'
}

export default {
  layout: 'TeflOrgHeader',

  async asyncData({ store, route, payload, error }) {
    console.log('payload: ', payload)
    try {
      // Fetch job data
      const page = payload || await store.dispatch('pages/getJob', route.path.slice(1, -1))
      console.log(route.path.slice(1, -1))


      // Generate SEO info for head
      // Normalize title
      const seoTitle =
        page.seo_title && page.seo_title.trim().length
          ? page.seo_title
          : page.title

      // Normalize description
      let seoDescription = 'Exciting job opportunity available!'
      if (page.seo_description && page.seo_description.trim().length) {
        seoDescription = page.seo_description
      } else if (page.location === 'Online') {
        seoDescription = 'Online teaching opportunity available'
      } else if (page.location && page.geolocationCountryLong) {
        seoDescription = `Teaching opportunity available in ${page.geolocationCountryLong}`
      }

      // Build structured data during SSR
      const structuredData = buildJobPostingSchema(page)
      const structuredDataBreadcrumbs = buildBreadcrumbSchema(page, route)

      return {
        page,
        seoTitle,
        seoDescription,
        // ...guideData,
        structuredData,
        structuredDataBreadcrumbs
      }
    } catch (err) {
      error({
        statusCode: 404,
        message: 'Job page could not be found'
      })
    }
  },

  data() {
    return {
      isUserLoggedIn: false,
      userHasPaid: false,
      customerData: null,
      guideData: {
        hasJobsGuide: false,
        hasCountryGuide: false,
        jobsGuideUrl: '',
        countryGuideUrl: '',
        countryName: ''
      }
    }
  },

  head() {
    return {
      title: `${this.seoTitle} | The TEFL Org`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.seoDescription
        }
      ],
      script: [
        {
          type: 'application/ld+json',
          json: this.structuredData
        },
        {
          type: 'application/ld+json',
          json: this.structuredDataBreadcrumbs
        }
      ]
    }
  },

  computed: {
    ...mapGetters({
      storeIsLoggedIn: 'user/isLoggedIn',
    }),

    // Generate optimised header image style
    headerImageStyle() {
      const dotIndex = this.page.thumbnail.lastIndexOf('.')
      const ext = this.page.thumbnail.substring(dotIndex)

      const options = { quality: 70, width: '1200px' }

      // Convert JPG to WebP
      if (['.jpg', '.JPG', '.jpeg', '.JPEG'].includes(ext)) {
        options.format = 'webp'
      }

      const imgUrl = this.page.thumbnail.includes('unsplash')
        ? this.page.thumbnail
        : this.$img(this.$options.filters.cdn(this.page.thumbnail), options)

      return {
        backgroundImage: `url('${imgUrl}')`
      }
    },
  },

  async mounted () {
    // Sync local state with store
    this.isUserLoggedIn = this.storeIsLoggedIn

    // Check payment status if logged in
    if (this.isUserLoggedIn) {
      this.checkHasPaid()
    }

    // Build guide URLs if location data exists
      if (this.page.geolocationCountryLong) {
        const country = this.page.geolocationCountryLong.toLowerCase().replace(/ +/g, '-')
        const countryGuideUrl = `/teach-english-abroad/teach-english-in-${country}/`

        // Special case for South Korea
        const jobsGuideUrl = country === 'south-korea'
          ? '/teach-english-abroad/teach-english-in-south-korea/tefl-jobs/'
          : `/tefl-jobs-centre/countries/tefl-jobs-in-${country}/`

        // Check if guide pages exist (in parallel)
        const [jobsGuideExists, countryGuideExists] = await Promise.all([
          checkPageExists(this.$axios, jobsGuideUrl),
          checkPageExists(this.$axios, countryGuideUrl)
        ])

        this.guideData = {
          hasJobsGuide: jobsGuideExists,
          hasCountryGuide: countryGuideExists,
          jobsGuideUrl,
          countryGuideUrl,
          countryName: country.replace(/-/g, ' ')
        }
      }
  },

  methods: {
    // Remove <br> tags from content
    cleanContent(content) {
      if (!content) return ''
      return content.replace(/<br\s*\/?>/gi, ' ')
    },

    // Check if user has purchased a course
    async checkHasPaid() {
      if (!this.$store.state.user.isLoggedIn) {
        return
      }

      try {
        this.customerData = await this.$axios.$get(location.protocol+'//'+location.host + '/.netlify/functions/me', {
          headers: {
            Authorization: this.$store.state.user.jwt_token
          }
        })

        this.userHasPaid = this.customerData?.hasPaid || false
      } catch (error) {
        console.error('Error checking payment status:', error)
        this.userHasPaid = false
      }
    }
  }
}

// Helper function to check if a page exists
async function checkPageExists($axios, url) {
  try {
    const result = await $axios.$post(location.protocol+'//'+location.host + '/.netlify/functions/checkPage', { id: url })
    return !!result?.id
  } catch (error) {
    console.error(`Error checking page ${url}:`, error)
    return false
  }
}

// Build JobPosting structured data schema
function buildJobPostingSchema(page) {
  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title: page.title,
    employmentType: JOB_TYPE_ENUM[page.jtype.replace(' ', '_')],
    datePosted: formatDateForSchema(page.date),
    description: page.content,
    identifier: {
      '@type': 'PropertyValue',
      name: page.company_name,
      value: page.reference
    },
    directApply: false,
    industry: 'Education',
    qualifications: `${page.certificate_hours || ''} TEFL certificate required`,
    validThrough: page.expiry || getDateOneMonthFromNow(),
    hiringOrganization: {
      '@type': 'Organization',
      name: page.company_name
    },
    image: page.thumbnail
  }

  // Add location data
  if (page.location === 'Online') {
    schema.jobLocationType = 'TELECOMMUTE'
    schema.applicantLocationRequirements = [
      { '@type': 'Country', name: 'UK' },
      { '@type': 'Country', name: 'USA' }
    ]
  } else if (page.geolocationCity && page.geolocationCountryLong) {
    schema.jobLocation = {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: page.geolocationCity,
        addressCountry: page.geolocationCountryLong
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: page.geolocationLat,
        longitude: page.geolocationLong
      }
    }
  }
  else if (!page.geolocationCity && page.geolocationCountryLong){
    schema.jobLocation = {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressCountry: page.geolocationCountryLong
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: page.geolocationLat,
        longitude: page.geolocationLong
      }
    }
  }

  // Add education requirements
  if (page.degree === '1') {
    schema.educationRequirements = {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'bachelor degree'
    }
  }

  // Add salary data
  if (page.salary && page.salary_number) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: page.salary_currency,
      value: {
        type: 'QuantitativeValue',
        value: page.salary_number,
        unitText: SALARY_PER_ENUM[page.salary_per]
      }
    }
  }

  return schema
}

// Build breadcrumb structured data schema
function buildBreadcrumbSchema(page, route) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': 'https://www.tefl.org/tefl-jobs-centre/',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.tefl.org/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'TEFL Jobs Centre',
        item: 'https://www.tefl.org/tefl-jobs-centre/'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'TEFL Jobs Centre Job Posting',
        item: 'https://www.tefl.org/tefl-jobs-centre/job/'
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: `Job Posting: ${page.title}`,
        item: `https://www.tefl.org${route.path}`
      }
    ]
  }
}

// Format date for schema (YYYY-MM-DD)
function formatDateForSchema(date) {
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Get date one month from now
function getDateOneMonthFromNow() {
  const date = new Date()
  date.setMonth(date.getMonth() + 1)
  return formatDateForSchema(date)
}
